"use client";

import { useRef } from "react";
import { MarginMode } from "@cede/types";
import { EXCHANGES, parsePairSymbol } from "@cede/utils";
import { useCexPerpsPositions } from "./useCexPerpsPositions";
import { useCexPerpsTradingFormStore } from "@/store/cex-perps/cexPerpsTradingFormStore";

interface UseAutoSelectMarginModeParams {
  exchangeId: string;
  accountId: number | undefined;
  /** The pair symbol to match for exchanges with per-symbol margin mode (e.g., Kraken) */
  pairSymbol?: string;
  enabled?: boolean;
}

/**
 * Hook to auto-select margin mode based on existing positions.
 *
 * Behavior varies by exchange:
 * - **Bybit**: All positions share the same margin mode (account-level setting).
 *   Uses any position's margin mode regardless of the pairSymbol param.
 * - **Kraken Futures**: Margin mode is per-symbol.
 *   Only auto-selects if there's a position matching the provided pairSymbol.
 *
 * The hook only updates the store once when positions are first loaded,
 * avoiding continuous overrides of user selections.
 */
export function useAutoSelectMarginMode({
  exchangeId,
  accountId,
  pairSymbol,
  enabled = true,
}: UseAutoSelectMarginModeParams) {
  const { positions, isLoading } = useCexPerpsPositions({
    exchangeId,
    accountId,
    enabled,
  });

  const setMarginMode = useCexPerpsTradingFormStore((state) => state.setMarginMode);
  const currentMarginMode = useCexPerpsTradingFormStore((state) => state.marginMode);

  // Track the key we last auto-selected for
  const lastAutoSelectedKeyRef = useRef<string | null>(null);

  // Compute the current key based on account, exchange, and positions state
  const currentKey = `${exchangeId}-${accountId}-${positions.length > 0}`;

  // Only auto-select if:
  // 1. Enabled
  // 2. Not loading
  // 3. Has positions
  // 4. Haven't already auto-selected for this key
  const shouldAutoSelect =
    enabled && !isLoading && positions.length > 0 && lastAutoSelectedKeyRef.current !== currentKey;

  let hasAutoSelected = lastAutoSelectedKeyRef.current === currentKey;

  if (shouldAutoSelect) {
    let inferredMarginMode: MarginMode | null = null;

    // Exchange-specific logic
    if (exchangeId === EXCHANGES.KRAKEN_FUTURES) {
      // Kraken Futures: Per-symbol margin mode - only match by pairSymbol
      if (pairSymbol) {
        const { base, quote } = parsePairSymbol(pairSymbol);
        const pairSymbolWithoutSettlement = `${base}/${quote}`;
        const matchingPosition = positions.find((p) => p.pairSymbol === pairSymbolWithoutSettlement);
        if (matchingPosition?.marginMode) {
          inferredMarginMode = matchingPosition.marginMode;
        }
      }
    } else {
      // Default behavior for other exchanges: use any position (like Bybit)
      const firstPosition = positions[0];
      if (firstPosition?.marginMode) {
        inferredMarginMode = firstPosition.marginMode;
      }
    }

    // Update store if we inferred a margin mode different from current
    if (inferredMarginMode && inferredMarginMode !== currentMarginMode) {
      setMarginMode(inferredMarginMode);
    }

    // Mark as auto-selected for this key
    lastAutoSelectedKeyRef.current = currentKey;
    hasAutoSelected = true;
  }

  return {
    /** Whether auto-selection has been performed */
    hasAutoSelected,
    /** Whether positions are still loading */
    isLoading,
    /** Number of positions used for inference */
    positionsCount: positions.length,
  };
}
